/**
 * Rephrasing for one text field.
 *
 * Scope is deliberately the single field being edited, never the whole
 * worksheet (Volume 5 section 5). Nothing here ever changes the field on its
 * own: a rephrasing is a proposal the teacher must explicitly accept before it
 * replaces anything (Volume 5 section 4). Rejecting leaves no trace - nothing
 * is saved, and the discarded suggestion is not recorded as accepted.
 *
 * A hook rather than a component because its two halves belong in two different
 * places: the button sits in the bar above the field, beside the field's own
 * label, and the result appears underneath where there is room to read it.
 */

import { useState, type ReactNode } from 'react'
import { Button } from '../../components/ui'
import { api } from '../../lib/api/client'
import { useAiStatus } from '../../lib/useAiStatus'

export type AiAssistParts = {
  /** The Rephrase button, for the bar above the field. */
  action: ReactNode
  /** The proposed rephrasing, for the row underneath. */
  panel: ReactNode
}

/** The four-pointed sparkle that marks a control as AI, used nowhere else so it
 *  keeps meaning one thing. */
function Sparkle() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.9 5.6a4 4 0 0 0 2.5 2.5L22 12l-5.6 1.9a4 4 0 0 0-2.5 2.5L12 22l-1.9-5.6a4 4 0 0 0-2.5-2.5L2 12l5.6-1.9a4 4 0 0 0 2.5-2.5L12 2z" />
      <path d="M19 2.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" opacity=".85" />
    </svg>
  )
}

export function useAiTextAssist({
  text,
  disabled,
  onApply,
}: {
  text: string
  disabled?: boolean
  onApply: (next: string) => void
}): AiAssistParts {
  const status = useAiStatus()
  const [rephrasing, setRephrasing] = useState(false)
  const [suggestion, setSuggestion] = useState<{ suggested: string; eventId: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const idle = { action: null, panel: null }
  if (disabled || !status.grammar_available) return idle

  const empty = !text.trim()

  const rephrase = async () => {
    setError(null)
    setSuggestion(null)
    setRephrasing(true)
    try {
      const result = await api.suggestGrammar(text)
      if (!result.available) {
        setError(result.reason || 'Rephrasing is unavailable right now.')
      } else if (!result.changed) {
        setError('No change suggested - this already reads clearly.')
      } else {
        setSuggestion({ suggested: result.suggested, eventId: result.usage_event_id ?? '' })
      }
    } catch {
      setError('Rephrasing is unavailable right now.')
    } finally {
      setRephrasing(false)
    }
  }

  const accept = () => {
    if (!suggestion) return
    onApply(suggestion.suggested)
    if (suggestion.eventId) void api.acceptGrammar(suggestion.eventId)
    setSuggestion(null)
  }

  const action = (
    <button
      type="button"
      className="tc-rephrase-btn"
      disabled={rephrasing || empty}
      title={
        empty
          ? 'Write something first'
          : 'Rewrite this as a simple question a pupil can understand'
      }
      // Keeps the teacher's cursor in the field while the request runs.
      onMouseDown={(event) => event.preventDefault()}
      onClick={rephrase}
    >
      <Sparkle />
      {rephrasing ? 'Rephrasing…' : 'Rephrase (AI)'}
    </button>
  )

  const panel =
    error || suggestion ? (
      <div className="tc-ai-assist-panel">
        {error && <div className="tc-ai-error">{error}</div>}

        {suggestion && (
          <div className="tc-ai-suggestion">
            <div className="tc-ai-suggestion-label">Suggested question</div>
            <p className="tc-ai-suggestion-text">{suggestion.suggested}</p>
            <div className="tc-ai-suggestion-actions">
              <Button size="sm" variant="primary" onClick={accept}>
                Use this
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSuggestion(null)}>
                Keep mine
              </Button>
            </div>
          </div>
        )}

        {typeof status.quota_text_remaining === 'number' && (
          <span className="tc-ai-quota-hint">
            {status.quota_text_remaining} rephrasings left this month
          </span>
        )}
      </div>
    ) : null

  return { action, panel }
}
